import { useCallback, useState } from 'react';

/** Device-local flag: the operator has seen the capture tips once. */
export const CAPTURE_TIPS_SEEN_KEY = 'aica.capture.tipsSeen';

/** The first-run guide, three short lines (DECISIONS.md item 4). */
export const CAPTURE_TIPS: readonly string[] = ['Fill the frame', 'Avoid glare', 'Hold steady'];

function readSeen(): boolean {
  try {
    return window.localStorage.getItem(CAPTURE_TIPS_SEEN_KEY) === '1';
  } catch {
    // Storage refused (private mode, quota). Treat the guide as seen, so a
    // device that cannot remember does not raise it on every visit.
    return true;
  }
}

function writeSeen(): void {
  try {
    window.localStorage.setItem(CAPTURE_TIPS_SEEN_KEY, '1');
  } catch {
    // Nothing to keep; the guide simply opens again next time.
  }
}

/**
 * The capture tips on the viewfinder. Open on the first visit from this
 * device, closed afterwards, and reopened from the Tips control whenever the
 * operator asks. Closing marks the guide seen.
 */
export function useCaptureTips(): { open: boolean; show: () => void; close: () => void } {
  const [open, setOpen] = useState(() => !readSeen());
  const show = useCallback(() => setOpen(true), []);
  const close = useCallback(() => {
    writeSeen();
    setOpen(false);
  }, []);
  return { open, show, close };
}
